import { useState } from 'react';
import { Loader2, X, Edit, FileText, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCandidateV2 } from '@/hooks/useCandidatesV2';
import CandidateActivitiesTabV2 from './CandidateActivitiesTabV2';
import CandidateNotesTabV2 from './CandidateNotesTabV2';
import CandidateStageHistoryTabV2 from './CandidateStageHistoryTabV2';

const STAGE_LABELS = {
  applied: 'Applied', resume_parsed: 'Resume Parsed', eligible: 'Eligible',
  exam_assigned: 'Exam Assigned', exam_completed: 'Exam Completed',
  shortlisted: 'Shortlisted', interview_scheduled: 'Interview Scheduled',
  offer: 'Offer', hired: 'Hired', rejected: 'Rejected',
};

const STAGE_COLORS = {
  applied: 'bg-gray-100 text-gray-700',
  resume_parsed: 'bg-blue-100 text-blue-700',
  eligible: 'bg-teal-100 text-teal-700',
  exam_assigned: 'bg-purple-100 text-purple-700',
  exam_completed: 'bg-indigo-100 text-indigo-700',
  shortlisted: 'bg-orange-100 text-orange-700',
  interview_scheduled: 'bg-yellow-100 text-yellow-700',
  offer: 'bg-green-100 text-green-700',
  hired: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const AVATAR_BG = [
  'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-red-500',
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
};

const getAvatarBg = (name) => {
  if (!name) return AVATAR_BG[0];
  return AVATAR_BG[name.charCodeAt(0) % AVATAR_BG.length];
};

const TABS = ['Summary', 'Resume', 'Activities', 'Notes', 'Stage History', 'Inbox'];

const Field = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b last:border-0 text-sm">
    <span className="text-gray-500 flex-shrink-0 mr-4">{label}</span>
    <span className="text-gray-800 text-right">{value || <span className="text-gray-300">—</span>}</span>
  </div>
);

const CandidateDrawerV2 = ({ candidateId, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = useState('Summary');

  const { data: candidate, isLoading } = useCandidateV2(candidateId);

  if (!candidateId) return null;

  const fullName = candidate
    ? [candidate.firstName, candidate.lastName].filter(Boolean).join(' ')
    : '';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 border-b flex-shrink-0">
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading...
            </div>
          ) : candidate ? (
            <>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0 ${getAvatarBg(fullName)}`}>
                {getInitials(fullName)}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-gray-900 text-base leading-tight">{fullName || 'Unnamed'}</h2>
                {candidate.currentTitle && (
                  <p className="text-sm text-gray-500">{candidate.currentTitle}{candidate.currentCompany ? ` · ${candidate.currentCompany}` : ''}</p>
                )}
                {candidate.stage && (
                  <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium ${STAGE_COLORS[candidate.stage] || 'bg-gray-100 text-gray-600'}`}>
                    {STAGE_LABELS[candidate.stage] || candidate.stage}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => onEdit(candidate)}>
                  <Edit className="h-3 w-3" /> Edit
                </Button>
                <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 text-sm text-gray-400">Candidate not found</div>
          )}
          {!isLoading && !candidate && (
            <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100 text-gray-400">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b flex-shrink-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading...
            </div>
          ) : !candidate ? null : (
            <>
              {activeTab === 'Summary' && (
                <div className="space-y-1">
                  <Field label="Email" value={candidate.email} />
                  <Field label="Phone" value={candidate.phone} />
                  <Field label="Location" value={candidate.location} />
                  <Field label="Current Title" value={candidate.currentTitle} />
                  <Field label="Current Company" value={candidate.currentCompany} />
                  <Field label="Experience" value={candidate.experienceYears != null ? `${candidate.experienceYears} yrs` : null} />
                  <Field label="Source" value={candidate.source} />
                  <Field label="Owner" value={candidate.owner?.name || candidate.owner?.email} />
                  <Field label="Status" value={candidate.status} />
                  {candidate.summary && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Summary</p>
                      <p className="text-sm text-gray-700">{candidate.summary}</p>
                    </div>
                  )}
                  {candidate.tags?.length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Tags</p>
                      <div className="flex flex-wrap gap-1">
                        {candidate.tags.map((t) => (
                          <span key={t} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {candidate.skills?.length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs text-gray-500 mb-1">Skills</p>
                      <div className="flex flex-wrap gap-1">
                        {candidate.skills.map((s) => (
                          <span key={s} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'Resume' && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  {candidate.resumeUrl ? (
                    <a
                      href={candidate.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-blue-600 hover:underline text-sm"
                    >
                      <FileText className="h-5 w-5" /> View Resume
                    </a>
                  ) : (
                    <>
                      <FileText className="h-10 w-10 mb-2 opacity-30" />
                      <p className="text-sm">No resume uploaded</p>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'Activities' && <CandidateActivitiesTabV2 candidateId={candidateId} />}
              {activeTab === 'Notes' && <CandidateNotesTabV2 candidateId={candidateId} />}
              {activeTab === 'Stage History' && <CandidateStageHistoryTabV2 candidateId={candidateId} />}

              {activeTab === 'Inbox' && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Mail className="h-10 w-10 mb-2 opacity-30" />
                  <p className="text-sm font-medium">No messages yet</p>
                  <p className="text-xs mt-1">Email integration coming soon</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CandidateDrawerV2;
